import type { IAvailabilityTableData } from './types';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_PER_WEEK = 7;

export const DEFAULT_AVAILABILITY_ROW: IAvailabilityTableData = {
  heading_1: '',
  heading_2: '',
  from_time: '00:00',
  to_time: '00:00',
  break_time: 0,
  total_time: 0
};

type AvailabilityTableInput = Record<string, IAvailabilityTableData> | null | undefined;

const getAvailabilityWeekDuration = (
  availabilityTableData: Record<string, IAvailabilityTableData>,
  weekDuration?: string
): number => {
  if (weekDuration === 'weekly') return 7;
  if (weekDuration === 'fortnightly') return 14;

  const numericOffsets = Object.keys(availabilityTableData)
    .map((key) => Number(key))
    .filter((offset) => Number.isInteger(offset));

  if (numericOffsets.length === 0) return 7;

  const hasZeroBasedOffsets = numericOffsets.includes(0);
  const normalizedOffsets = hasZeroBasedOffsets
    ? numericOffsets.map((offset) => offset + 1)
    : numericOffsets;

  return Math.max(...normalizedOffsets) > DAYS_PER_WEEK ? 14 : 7;
};

export const normalizeAvailabilityTableData = (
  availabilityTableData: AvailabilityTableInput,
  weekDuration?: string
): Record<string, IAvailabilityTableData> => {
  const tableData = availabilityTableData ?? {};
  const hasZeroBasedOffsets = Object.keys(tableData).some((key) => Number(key) === 0);
  const normalizedOffsetsData = Object.entries(tableData).reduce<
    Record<string, IAvailabilityTableData>
  >((acc, [key, value]) => {
    const numericKey = Number(key);
    if (Number.isNaN(numericKey)) return acc;

    const normalizedKey = hasZeroBasedOffsets ? String(numericKey + 1) : String(numericKey);
    acc[normalizedKey] = value;
    return acc;
  }, {});

  const totalDays = getAvailabilityWeekDuration(tableData, weekDuration);

  return Array.from({ length: totalDays }).reduce<Record<string, IAvailabilityTableData>>(
    (acc, _, index) => {
      const dayOffset = String(index + 1);
      acc[dayOffset] = normalizedOffsetsData[dayOffset] ?? { ...DEFAULT_AVAILABILITY_ROW };
      return acc;
    },
    {}
  );
};

export interface IAvailabilityWeekSection {
  weekNumber: number;
  rows: {
    absoluteOffset: number;
    payrollDay: number;
    day: string;
    data: IAvailabilityTableData;
  }[];
  totalHours: number;
  totalShifts: number;
}

export const buildAvailabilityWeekSections = (
  availabilityTableData: AvailabilityTableInput,
  weekStartDay: number,
  weekDuration?: string
): IAvailabilityWeekSection[] => {
  const normalizedTableData = normalizeAvailabilityTableData(availabilityTableData, weekDuration);
  const totalDays = Object.keys(normalizedTableData).length;
  const totalWeeks = Math.ceil(totalDays / DAYS_PER_WEEK);
  const normalizedWeekStartDay = Number(weekStartDay) || 0;

  return Array.from({ length: totalWeeks }, (_week, weekIndex) => {
    const weekNumber = weekIndex + 1;
    const weekStartOffset = weekIndex * DAYS_PER_WEEK + 1;
    const weekEndOffset = Math.min(weekStartOffset + DAYS_PER_WEEK - 1, totalDays);

    const rows = Array.from({ length: weekEndOffset - weekStartOffset + 1 }, (_row, rowIndex) => {
      const absoluteOffset = weekStartOffset + rowIndex;
      return {
        absoluteOffset,
        payrollDay: rowIndex + 1,
        day: DAY_NAMES[(rowIndex + normalizedWeekStartDay) % DAY_NAMES.length].slice(0, 3),
        data: normalizedTableData[String(absoluteOffset)]
      };
    });

    const totalHours =
      Math.round(rows.reduce((acc, row) => acc + (row.data.total_time || 0), 0) * 100) / 100;
    const totalShifts = rows.filter((row) => row.data.total_time !== 0).length;

    return {
      weekNumber,
      rows,
      totalHours,
      totalShifts
    };
  });
};
